import React from 'react';
import styled from 'styled-components';

import { ApiBalanceHistoryEvent } from '#/api/types';
import Button from '#/components/basic/Button/Button';
import { ButtonCenteredContent } from '#/components/basic/Button/ButtonCentedContent';
import SimpleModalOrMobilePage from '#/components/basic/modal/SimpleModalOrMobilePage';
import DataPair from '#/components/basic/panels/DataPair';
import { StargateExplorerDoubleIcon } from '#/components/wallet/icons/explorer/StargateExplorerDoubleIcon';
import config from '#/config';
import { ChainsFromConfig } from '#/config/configType';
import { getBlockExplorerTransactionUrl } from '#/config/configUtils';
import { Chains } from '#/config/constants';
import { formatDateTime } from '#/lib/date';
import { formatUsd } from '#/lib/display/formatting';
import { openExternalUrl } from '#/lib/redirect';
import { capitalizeFirstLetter } from '#/lib/string';

import { convertApiBridge } from '../bridge/crossChainMapping';
import { getStargateScannerTxUrl } from '../bridge/utils';
import { getChainIcon } from '../chains/chainIcons/chainIcons';

import BalanceTransactionChain from './BalanceTransactionChain';
import BalanceTransactionFee from './BalanceTransactionFee';
import BalanceTransactionStatuses from './BalanceTransactionStatuses';
import BalanceTransactionType from './BalanceTransactionType';
import { detectTransactionBridge } from './utils';

const ButtonsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

function ButtonChainTransactionLink({
  chain,
  link,
}: {
  chain: ChainsFromConfig;
  link: string;
}) {
  return (
    <Button onClick={() => openExternalUrl(link)} bordered>
      <ButtonCenteredContent>
        <img src={getChainIcon(chain)} height={12} />{' '}
        {config.chains[chain].chainNetwork} Transaction
      </ButtonCenteredContent>
    </Button>
  );
}

function ButtonStargateLink({
  link,
  chain,
  chainType,
}: {
  link: string;
  chain: ChainsFromConfig | undefined;
  chainType: 'source' | 'target';
}) {
  return (
    <Button onClick={() => openExternalUrl(link)} bordered>
      <ButtonCenteredContent>
        <StargateExplorerDoubleIcon
          chain={chain}
          chainType={chainType}
          size={12}
        />{' '}
        Stargate Bridge
        {/* {isForwarder && <>(Deposit)</>} */}
      </ButtonCenteredContent>
    </Button>
  );
}

export default function BalanceHistoryDetailMobile({
  transaction,
  activeChain,
  onClose,
  hideVaultAddress,
}: {
  transaction: ApiBalanceHistoryEvent;
  activeChain: Chains;
  onClose: () => void;
  /** E.g. on vault detail page where all events belong to the same vault */
  hideVaultAddress?: boolean;
}) {
  const bridge = detectTransactionBridge(transaction);
  return (
    <SimpleModalOrMobilePage
      size="small"
      title={
        transaction.type === 'pendingDeposit'
          ? 'Pending Deposit'
          : capitalizeFirstLetter(transaction.type)
      }
      onBack={onClose}
      verticalAlignOnMobile="centered"
    >
      <div>
        <DataPair label="Date">{formatDateTime(transaction.time)}</DataPair>

        <DataPair label="Type" altBackground childrenFontFamily="standard">
          <BalanceTransactionType
            transaction={transaction}
            vaultAddressType={hideVaultAddress ? 'hidden' : 'link'}
          />
        </DataPair>

        <DataPair label="Quantity">
          {formatUsd(transaction.quantity)} USDC
        </DataPair>

        {/* <DataPair label="Bridge" darkerBackground childrenFontFamily="standard">
          <BalanceTransactionBridge transaction={transaction} />
        </DataPair> */}

        <DataPair label="Chain" altBackground childrenFontFamily="standard">
          <BalanceTransactionChain transaction={transaction} />
        </DataPair>

        <DataPair label="Fee">
          <BalanceTransactionFee transaction={transaction} />
        </DataPair>

        <DataPair label="Status" altBackground childrenFontFamily="standard">
          <BalanceTransactionStatuses transaction={transaction} />
        </DataPair>

        <ButtonsWrap>
          {/* Pending source chain (loopback deposits have no on-chain source TX) */}
          {transaction.type === 'pendingDeposit' && !transaction.loopback && (
            <ButtonChainTransactionLink
              chain={transaction.sourceChain}
              link={getBlockExplorerTransactionUrl(
                transaction.sourceTxId,
                config.chains[transaction.sourceChain]
              )}
            />
          )}

          {/* Persisted Deposit/Withdrawal transaction */}
          {transaction.type !== 'pendingDeposit' && transaction.katanaTxId && (
            <ButtonChainTransactionLink
              chain="katanaChain"
              link={getBlockExplorerTransactionUrl(
                transaction.katanaTxId,
                activeChain
              )}
            />
          )}

          {/* Pending transaction */}
          {transaction.type === 'pendingDeposit' && transaction.bridgeTxId && (
            <ButtonChainTransactionLink
              chain="katanaChain"
              link={getBlockExplorerTransactionUrl(
                transaction.bridgeTxId,
                activeChain
              )}
            />
          )}

          {/* Forwarder Layer0 scanner link */}
          {bridge &&
            transaction.type === 'pendingDeposit' &&
            transaction.forwarderTxId && (
              <ButtonStargateLink
                link={getStargateScannerTxUrl(transaction.forwarderTxId)}
                chain="ethereum"
                chainType="source"
              />
            )}
          {bridge &&
            transaction.type === 'deposit' &&
            transaction.forwarderTxId && (
              <ButtonStargateLink
                link={getStargateScannerTxUrl(transaction.forwarderTxId)}
                chain="ethereum"
                chainType="source"
              />
            )}

          {/* Bridge link */}
          {bridge &&
            transaction.type === 'deposit' &&
            transaction.bridgeTxId && (
              <ButtonStargateLink
                link={getStargateScannerTxUrl(transaction.bridgeTxId)}
                chain={convertApiBridge(transaction.bridgeSource).targetChain}
                chainType="source"
              />
            )}
          {bridge && transaction.type === 'pendingDeposit' && (
            <ButtonStargateLink
              link={getStargateScannerTxUrl(transaction.sourceTxId)}
              chain={transaction.sourceChain}
              chainType="source"
            />
          )}
          {bridge &&
            transaction.type === 'withdrawal' &&
            transaction.katanaTxId && (
              <ButtonStargateLink
                link={getStargateScannerTxUrl(transaction.katanaTxId)}
                chain="beraChain"
                chainType="target"
              />
            )}
        </ButtonsWrap>
      </div>
    </SimpleModalOrMobilePage>
  );
}
